import React, { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Empty } from "antd";
import {
  Bell,
  Share2,
  MessageCircle,
  Sparkles,
  AlertTriangle
} from "lucide-react";
import { Notification } from "../../types/dbTypes";

export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const notificationIcon: Record<
  Notification["type"],
  React.ComponentType<{ size?: number; className?: string }>
> = {
  share: Share2,
  comment: MessageCircle,
  ai_complete: Sparkles,
  spending_limit: AlertTriangle,
  broadcast: Bell,
  workflow_assignment: Bell,
  workflow_artifact_ready: Sparkles
};

export const NotificationPanel: React.FC<{
  notifications: Notification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  onClose: () => void;
}> = ({ notifications, unreadCount, markAllRead, onClose }) => {
  const navigate = useNavigate();

  const handleClick = useCallback(
    (n: Notification) => {
      navigate(`/notifications/${n._id}`, { state: { notification: n } });
      onClose();
    },
    [navigate, onClose]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="flex items-center justify-between mb-3">
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={markAllRead}>
            Mark all read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications yet"
          style={{ margin: "16px 0" }}
        />
      ) : (
        <div style={{ flex: 1, overflowY: "auto" }}>
          {notifications.map((n) => {
            const Icon = notificationIcon[n.type] ?? Bell;
            return (
              <div
                key={n._id}
                onClick={() => handleClick(n)}
                className={`flex gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 transition-colors ${
                  n.is_read ? "opacity-60" : ""
                }`}
              >
                <div className="mt-0.5 shrink-0">
                  <Icon size={16} className="text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-snug truncate">
                    {n.title}
                  </p>
                  <p className="text-xs text-gray-500 leading-snug line-clamp-2">
                    {n.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {timeAgo(n.created_at)}
                  </p>
                </div>
                {!n.is_read && (
                  <div className="w-2 h-2 rounded-full bg-purple-600 mt-1.5 shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-gray-100 text-center">
        <Link
          to="/notifications"
          className="text-xs text-purple-600 hover:underline"
          onClick={onClose}
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
};
